import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from '../sp-firebase/firebase';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8080';

export type RicMethod = 'monthly' | 'terminal_groups' | 'continuation' | 'spot_only';

export interface ContractAdminDto {
  id: string;
  code: string;
  name: string;
  type: 'material' | 'fx' | 'freight' | 'index' | 'synthetic';
  currency: string;
  quoteUnit: string;
  /** Multiplier applied to vendor quotes at ingestion (e.g. 10 for EUR/100kg -> EUR/MT) */
  unitMultiplier: number;
  /** Unit the vendor actually quotes in when it differs from quoteUnit (e.g. EUR/100kg) */
  sourceUnit: string | null;
  region?: string;
  commodityGroup?: string;
  deliveryPoint?: string;
  active: boolean;

  quoteSourceId: string;
  provider: 'refinitiv' | 'bloomberg' | 'manual' | string;
  symbol: string;
  fetchEnabled: boolean;

  // RIC construction config (contract_fetch_configs)
  ricMethod: RicMethod | null;
  baseSymbol: string | null;
  ricYearStyle: '1_digit' | '2_digit' | null;
  spotSuffix: string | null;
  tradingCalendar: number[] | null;
  futuresDepth: number | null;
  /** Derived from ricMethod on the server (read-only) */
  fetchMode: 'spot_only' | 'futures_curve' | null;

  lastFetchStatus: 'success' | 'failed' | 'in_progress' | 'never';
  lastFetchAt: string | null;
  lastFetchError: string | null;

  latestPrice: number | null;
  latestPriceDate: string | null;
  firstPriceDate: string | null;
}

export interface CreateContractDto {
  code: string;
  name: string;
  type: 'material' | 'fx' | 'freight' | 'index' | 'synthetic';
  currency: string;
  quoteUnit: string;
  unitMultiplier?: number;
  sourceUnit?: string | null;
  region?: string;
  commodityGroup?: string;
  deliveryPoint?: string;
  active?: boolean;

  provider: 'refinitiv' | 'bloomberg' | 'manual' | string;
  symbol: string;
  fetchEnabled?: boolean;

  // RIC construction config
  ricMethod: RicMethod;
  baseSymbol?: string;
  ricYearStyle?: '1_digit' | '2_digit' | null;
  spotSuffix?: string | null;
  tradingCalendar?: number[] | null;
  futuresDepth?: number;
}

export interface UpdateContractDto extends Partial<CreateContractDto> {
  id: string;
}

async function waitForFirebaseUser(timeoutMs: number = 5000): Promise<User | null> {
  if (auth.currentUser) {
    return auth.currentUser;
  }

  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      resolve(user);
    });

    setTimeout(() => {
      unsubscribe();
      resolve(null);
    }, timeoutMs);
  });
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const user = await waitForFirebaseUser();
  
  if (!user) {
    throw new Error('Not authenticated');
  }

  const token = await user.getIdToken();
  
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

export async function getContracts(): Promise<ContractAdminDto[]> {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_URL}/api/v2/contracts`, {
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to fetch contracts');
  }

  const data = await response.json();
  return data.contracts as ContractAdminDto[];
}

export async function getContract(id: string): Promise<ContractAdminDto> {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_URL}/api/v2/contracts/${id}`, {
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to fetch contract');
  }

  const data = await response.json();
  return data.contract as ContractAdminDto;
}

export async function createContract(contract: CreateContractDto): Promise<ContractAdminDto> {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_URL}/api/v2/contracts`, {
    method: 'POST',
    headers,
    body: JSON.stringify(contract),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to create contract');
  }

  const data = await response.json();
  return data.contract as ContractAdminDto;
}

export async function updateContract(id: string, updates: Partial<CreateContractDto>): Promise<ContractAdminDto> {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_URL}/api/v2/contracts/${id}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to update contract');
  }

  const data = await response.json();
  return data.contract as ContractAdminDto;
}

export async function toggleContractFetch(id: string, enabled: boolean): Promise<void> {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_URL}/api/v2/contracts/${id}/fetch-enabled`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify({ fetchEnabled: enabled }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to toggle fetch');
  }
}

export async function deleteContract(id: string): Promise<void> {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_URL}/api/v2/contracts/${id}`, {
    method: 'DELETE',
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to delete contract');
  }
}
